<script setup lang="ts">
import type { Schema } from "../epost-login.js";
import defaults from "../epostLoginDefaults";
import { submitFormToHostopia } from "../../common/epostUtils.js";
import { useSessionStorage } from "@vueuse/core";

const props = defineProps<{
	content: Schema["content"];
}>();

const showForm = defineModel<boolean>("showForm", { default: false });

const username = useSessionStorage<string>("username", "");
const password = ref<string>("");

const errorMessage = ref<string | null>(null);
const showError = (msg: string) => {
	errorMessage.value = msg;
	nextTick(() => {
		const alert = document.querySelector('[role="alert"]');
		if (alert) (alert as HTMLElement).focus();
	});
};

const isSubmitting = ref(false);
const handleFormLogin = (event: SubmitEvent) => {
	event.preventDefault();
	errorMessage.value = null;
	isSubmitting.value = true;

	try {
		submitFormToHostopia(username.value, password.value);
	} catch (err) {
		console.error("Form submission failed:", err);
		errorMessage.value = "Noe gikk galt under innsending. Prøv igjen.";
		isSubmitting.value = false;
	}
};

onMounted(() => {
	const params = new URLSearchParams(window.location.search);
	if (params.get("loginError") === "1") {
		showError(props.content.errors?.errorInvalidCredentials || defaults.errors.errorInvalidCredentials);
	}
});
</script>

<template>
	<div>
		<div class="spacing-container" />
		<button
			class="login-email-form-header"
			@click="showForm = !showForm"
			:aria-expanded="showForm"
		>
			<span class="drawer-text">{{ content.form.drawerText }}</span>
			<div>
				<TnIcon
					class="chevron-icon"
					name="chevron-down"
					size="l"
				/>
			</div>
		</button>
		<Transition
			name="slide"
			mode="out-in"
		>
			<div v-show="showForm">
				<TnNotification
					v-if="content.loginInfoBox.showInfoBox"
					hide-icon
					:title="content.loginInfoBox?.loginInfoBoxTitle || defaults.loginInfoBox.loginInfoBoxTitle"
				>
					<span>{{ content.loginInfoBox?.loginInfoBoxIngress || defaults.loginInfoBox.loginInfoBoxIngress }}</span>
					<TnLink
						:to="content.loginInfoBox?.loginInfoBoxUrl || defaults.loginInfoBox.loginInfoBoxUrl"
						:target="content.loginInfoBox?.openInfoBoxInSameTab ? '_self' : '_blank'"
					>
						{{ content.loginInfoBox?.loginInfoBoxUrlText || defaults.loginInfoBox.loginInfoBoxUrlText }}
					</TnLink>
				</TnNotification>
				<TnNotification
					v-if="errorMessage || (content.form.showWarningText && content.form.formWarningText)"
					class="margin-top-m"
					:type="errorMessage ? 'error' : 'warning'"
					:role="errorMessage ? 'alert' : 'status'"
					tabindex="-1"
					dark
					full-width
				>
					<div>{{ errorMessage ? errorMessage : content.form?.formWarningText }}</div>
				</TnNotification>
				<form
					id="loginemailform"
					class="login-email-form"
					autocomplete="on"
					@submit.prevent.stop="handleFormLogin"
				>
					<TnTextField
						v-model="username"
						:dark="true"
						size="m"
						type="email"
						label="E-postadresse:"
						name="user"
						placeholder="dinepost@online.no"
						id="email-username"
						class="margin-top-xl"
						:clearable="true"
						required
						aria-label="E-postadresse"
						autocomplete="email"
					/>
					<TnTextField
						v-model="password"
						:dark="true"
						size="m"
						type="password"
						label="Passord:"
						name="password"
						placeholder="Ditt online passord"
						id="email-password"
						class="margin-top-m"
						:clearable="true"
						required
						aria-label="E-post passord"
						autocomplete="current-password"
					/>
					<div class="margin-top-l bottom-cta-group">
						<TnButton
							class="login-submit-button"
							:primary="true"
							:dark="true"
							:disabled="isSubmitting"
							type="submit"
						>
							<span v-if="!isSubmitting">
								{{ content.form?.loginButtonText || defaults.form.loginButtonText }}
							</span>
							<span v-else>
								{{ content.form?.loggingInButtonText || defaults.form.loggingInButtonText }}
								<TnSpinner
									size="s"
									color="lightblue"
								/>
							</span>
						</TnButton>
						<TnLink
							class="margin-top-s"
							:dark="true"
							:text="content.form?.forgotPasswordText || defaults.form.forgotPasswordText"
							:to="content.form?.forgotPasswordUrl || defaults.form.forgotPasswordUrl"
						/>
					</div>
				</form>
			</div>
		</Transition>
	</div>
</template>

<style scoped lang="scss">
.login-email-form-header {
	border: none;
	background-color: transparent;
	color: #fff;
	@include font-title-2xs;

	margin: auto;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 1rem;
	cursor: pointer;
	& > div:first-child {
		margin-bottom: 1rem;
	}
}

.slide-enter-active,
.slide-leave-active {
	transition:
		max-height 0.3s,
		opacity 0.3s;
	max-height: 100vh;
}
.slide-enter-from,
.slide-leave-to {
	max-height: 0;
	overflow: hidden;
	opacity: 0;
}

.login-email-form {
	display: flex;
	flex-direction: column;
}

.login-submit-button span {
	display: flex;
	align-items: center;
}

.login-submit-button .spinner {
	margin-left: 8px;
}

.bottom-cta-group {
	display: flex;
	flex-direction: column;
}

.separator {
	display: flex;
	align-items: center;
	text-align: center;

	&::before,
	&::after {
		content: "";
		flex: 1;
		height: 1px;
		background-color: #c5cbdd;
	}

	span {
		margin: 0 48px;
		font-size: 16px;
	}
}

.login-email-form-header {
	margin-top: $spacing-xl;
	transition: margin-top 0.3s ease;
}
.login-email-form-header[aria-expanded="true"] {
	margin-top: 0;
}

.login-email-form-header .chevron-icon {
	transition: transform 0.3s cubic-bezier(0.4, 0.2, 0.2, 1);
	transform: scaleY(1);
}
.login-email-form-header[aria-expanded="true"] .chevron-icon {
	transform: scaleY(-1);
}

.login-email-form-header .drawer-text {
	transition:
		opacity 0.3s,
		max-height 0.3s;
	opacity: 1;
	max-height: 100px;
	overflow: hidden;
	margin-bottom: 8px;
}
.login-email-form-header[aria-expanded="true"] .drawer-text {
	opacity: 0;
	max-height: 0;
	pointer-events: none;
}
</style>
